// section-navigation-buttons-2.js

(function() {
  'use strict';

  // ========================================
  // Configuration
  // ========================================
  const SELECTORS = {
    section: '.nav-buttons-2',
    buttons: '.section-nav-button .nav-button',
    scrollWrapper: '.nav-scroll-wrapper'
  };

  const CONFIG = {
    viewThreshold: 100,
    initDelay: 100,
    scrollDebounce: 10,
    resizeDebounce: 150
  };

  // ========================================
  // Main Class
  // ========================================
  class NavigationButtons {
    constructor(section) {
      this.section = section;
      this.buttons = section.querySelectorAll(SELECTORS.buttons);
      this.scrollWrapper = section.querySelector(SELECTORS.scrollWrapper);
      
      if (!this.buttons.length) return;
      
      this.navOffsetTop = 0;
      this.scrollTimer = null;
      this.resizeTimer = null;
      this.isInitialized = false;
      
      // Build anchor map
      this.anchorMap = this._buildAnchorMap();
      
      // Initialize
      this.init();
    }

    /**
     * Build map of buttons to their target elements
     */
    _buildAnchorMap() {
      return Array.from(this.buttons)
        .map((btn) => {
          const href = btn.getAttribute('href');
          if (!href || href === '#') return null;
          const target = document.querySelector(href);
          return target ? { btn, target } : null;
        })
        .filter(Boolean);
    }

    /**
     * Initialize navigation offset position
     */
    _initOffset() {
      const rect = this.section.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      this.navOffsetTop = rect.top + scrollTop;
    }

    /**
     * Check if section should be sticky
     */
    _checkSticky() {
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      const isSticky = scrollY > this.navOffsetTop;
      this.section.classList.toggle('is-sticky', isSticky);
    }

    /**
     * Update active button based on scroll position
     */
    _updateActiveButton() {
      if (!this.anchorMap.length) return;

      const viewportHeight = window.innerHeight;
      let activeIndex = 0;

      // Find the last visible anchor
      for (let i = this.anchorMap.length - 1; i >= 0; i--) {
        const { target } = this.anchorMap[i];
        const rect = target.getBoundingClientRect();
        const isVisible = rect.bottom > CONFIG.viewThreshold && 
                         rect.top < viewportHeight - CONFIG.viewThreshold;

        if (isVisible) {
          activeIndex = i;
          break;
        }
      }

      // Update button states
      this.buttons.forEach((btn) => {
        btn.classList.remove('active');
        btn.setAttribute('aria-selected', 'false');
      });
      
      if (this.anchorMap[activeIndex]) {
        const { btn } = this.anchorMap[activeIndex];
        btn.classList.add('active');
        btn.setAttribute('aria-selected', 'true');
      }
    }

    /**
     * Handle scroll events (debounced)
     */
    _handleScroll = () => {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        this._checkSticky();
        this._updateActiveButton();
      }, CONFIG.scrollDebounce);
    };

    /**
     * Handle resize events (debounced)
     */
    _handleResize = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this._initOffset();
        this._updateActiveButton();
      }, CONFIG.resizeDebounce);
    };

    /**
     * Handle button click - smooth scroll to target
     */
    _handleButtonClick = (e) => {
      const button = e.currentTarget;
      const href = button.getAttribute('href');
      const target = document.querySelector(href);
      
      if (!target) return;
      
      e.preventDefault();
      
      // Update active state immediately
      this.buttons.forEach((btn) => {
        btn.classList.remove('active');
        btn.setAttribute('aria-selected', 'false');
      });
      button.classList.add('active');
      button.setAttribute('aria-selected', 'true');
      
      // Smooth scroll to target
      target.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    };

    /**
     * Setup event listeners
     */
    _setupListeners() {
      // Button clicks
      this.buttons.forEach((button) => {
        button.addEventListener('click', this._handleButtonClick);
      });

      // Scroll and resize
      window.addEventListener('scroll', this._handleScroll, { passive: true });
      window.addEventListener('resize', this._handleResize, { passive: true });
    }

    /**
     * Initialize the component
     */
    init() {
      if (this.isInitialized) return;
      
      // Set initial state
      this._initOffset();
      this._updateActiveButton();
      
      // Setup listeners
      this._setupListeners();
      
      // Confirm position after layout settle
      setTimeout(() => {
        this._initOffset();
        this._checkSticky();
      }, CONFIG.initDelay);
      
      this.isInitialized = true;
    }

    /**
     * Clean up event listeners
     */
    destroy() {
      this.buttons.forEach((button) => {
        button.removeEventListener('click', this._handleButtonClick);
      });
      
      window.removeEventListener('scroll', this._handleScroll);
      window.removeEventListener('resize', this._handleResize);
      
      clearTimeout(this.scrollTimer);
      clearTimeout(this.resizeTimer);
      
      this.isInitialized = false;
    }
  }

  // ========================================
  // Initialize
  // ========================================
  const sections = document.querySelectorAll(SELECTORS.section);
  const instances = [];

  function initSections() {
    sections.forEach((section) => {
      // Check if already initialized
      if (section.dataset.navigationInitialized) return;
      
      const instance = new NavigationButtons(section);
      if (instance.isInitialized) {
        section.dataset.navigationInitialized = 'true';
        instances.push(instance);
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSections);
  } else {
    initSections();
  }

  // Shopify Theme Editor support
  if (window.Shopify && Shopify.theme) {
    document.addEventListener('shopify:section:load', (event) => {
      const section = event.target.querySelector(SELECTORS.section);
      if (section && !section.dataset.navigationInitialized) {
        const instance = new NavigationButtons(section);
        if (instance.isInitialized) {
          section.dataset.navigationInitialized = 'true';
          instances.push(instance);
        }
      }
    });

    document.addEventListener('shopify:section:unload', (event) => {
      const section = event.target.querySelector(SELECTORS.section);
      if (section) {
        const index = instances.findIndex(inst => inst.section === section);
        if (index !== -1) {
          instances[index].destroy();
          instances.splice(index, 1);
        }
      }
    });
  }

})();